export type CheckInLike = {
  mood: number;
  energy: number | null;
  stress: number | null;
  client_created_at: string;
};

export type DayPoint = {
  /** Chart axis label */
  label: string;
  dateKey: string;
  mood: number | null;
  energy: number | null;
  stress: number | null;
  count: number;
};

export type ChartRange = 7 | 30 | 90;

const DAY_LABELS = ["Sön", "Mån", "Tis", "Ons", "Tor", "Fre", "Lör"] as const;

function avg(nums: number[]): number | null {
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function localDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Daily series for the last N calendar days (oldest → newest).
 */
export function buildDaySeries(
  checkIns: CheckInLike[],
  dayCount: number
): DayPoint[] {
  const now = new Date();
  now.setHours(12, 0, 0, 0);
  const buckets = new Map<
    string,
    { moods: number[]; energies: number[]; stresses: number[]; label: string }
  >();

  for (let i = dayCount - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = localDayKey(d);
    let label: string;
    if (dayCount <= 7) {
      label = DAY_LABELS[d.getDay()] ?? "?";
    } else if (dayCount <= 31) {
      // Day-of-month; WeekChart can thin labels via showLabelEvery
      label = String(d.getDate());
    } else {
      label = `${d.getDate()}/${d.getMonth() + 1}`;
    }
    buckets.set(key, {
      moods: [],
      energies: [],
      stresses: [],
      label,
    });
  }

  for (const c of checkIns) {
    // Use local date from ISO if possible
    const raw = c.client_created_at.slice(0, 10);
    const b = buckets.get(raw);
    if (!b) continue;
    b.moods.push(c.mood);
    if (c.energy != null) b.energies.push(c.energy);
    if (c.stress != null) b.stresses.push(c.stress);
  }

  return Array.from(buckets.entries()).map(([key, b]) => ({
    label: b.label,
    dateKey: key,
    mood: avg(b.moods),
    energy: avg(b.energies),
    stress: avg(b.stresses),
    count: b.moods.length,
  }));
}

/** @deprecated use buildDaySeries(checkIns, 7) */
export function buildLast7Days(checkIns: CheckInLike[]): DayPoint[] {
  return buildDaySeries(checkIns, 7);
}

/**
 * Weekly averages for the last `weekCount` weeks (oldest → newest).
 * Used for longer ranges (e.g. 90 days ≈ 13 weeks).
 */
export function buildWeekSeries(
  checkIns: CheckInLike[],
  weekCount: number
): DayPoint[] {
  const now = new Date();
  now.setHours(12, 0, 0, 0);
  // End of current week = today; each bucket is 7 days ending on that day
  const points: DayPoint[] = [];

  for (let w = weekCount - 1; w >= 0; w--) {
    const end = new Date(now);
    end.setDate(end.getDate() - w * 7);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    const startKey = localDayKey(start);
    const endKey = localDayKey(end);

    const moods: number[] = [];
    const energies: number[] = [];
    const stresses: number[] = [];

    for (const c of checkIns) {
      const key = c.client_created_at.slice(0, 10);
      if (key < startKey || key > endKey) continue;
      moods.push(c.mood);
      if (c.energy != null) energies.push(c.energy);
      if (c.stress != null) stresses.push(c.stress);
    }

    points.push({
      label: `${start.getDate()}/${start.getMonth() + 1}`,
      dateKey: endKey,
      mood: avg(moods),
      energy: avg(energies),
      stress: avg(stresses),
      count: moods.length,
    });
  }

  return points;
}

export function buildChartSeries(
  checkIns: CheckInLike[],
  range: ChartRange
): DayPoint[] {
  if (range === 7) return buildDaySeries(checkIns, 7);
  if (range === 30) return buildDaySeries(checkIns, 30);
  return buildWeekSeries(checkIns, 13);
}

export type WeeklySummary = {
  checkInCount: number;
  experimentCount: number;
  avgMood: number | null;
  avgEnergy: number | null;
  avgStress: number | null;
  text: string;
};

export function buildWeeklySummary(input: {
  checkIns: CheckInLike[];
  experimentCompleted: number;
  topExperimentTitle?: string | null;
}): WeeklySummary {
  const weekStart = new Date();
  weekStart.setHours(0, 0, 0, 0);
  weekStart.setDate(weekStart.getDate() - 6);
  const startKey = localDayKey(weekStart);

  const weekCheckIns = input.checkIns.filter(
    (c) => c.client_created_at.slice(0, 10) >= startKey
  );

  const moods = weekCheckIns.map((c) => c.mood);
  const energies = weekCheckIns
    .map((c) => c.energy)
    .filter((v): v is number => v != null);
  const stresses = weekCheckIns
    .map((c) => c.stress)
    .filter((v): v is number => v != null);

  const round = (v: number | null) =>
    v == null ? null : Math.round(v * 10) / 10;

  const avgMood = round(avg(moods));
  const avgEnergy = round(avg(energies));
  const avgStress = round(avg(stresses));

  let text: string;
  if (weekCheckIns.length === 0 && input.experimentCompleted === 0) {
    text =
      "Den här veckan är en blank yta. En check-in eller ett litet experiment räcker för att starta din karta.";
  } else {
    const parts: string[] = [];
    parts.push(
      `Senaste sju dagarna: ${weekCheckIns.length} check-in${weekCheckIns.length === 1 ? "" : "s"} och ${input.experimentCompleted} experiment.`
    );
    if (avgEnergy != null && avgStress != null) {
      if (avgEnergy >= 6 && avgStress <= 4) {
        parts.push("Energin har legat bra och stressen hållit sig lugn.");
      } else if (avgStress >= 7) {
        parts.push(
          "Stressen har varit hög. Mjuka recovery-experiment kan vara extra värdefulla."
        );
      } else if (avgEnergy <= 4) {
        parts.push(
          "Energin har varit låg. Håll stegen korta och snälla mot dig själv."
        );
      } else {
        parts.push("Det finns variation i hur du mår. Det är helt okej.");
      }
    }
    if (input.topExperimentTitle) {
      parts.push(`Något som återkommer: “${input.topExperimentTitle}”.`);
    }
    text = parts.join(" ");
  }

  return {
    checkInCount: weekCheckIns.length,
    experimentCount: input.experimentCompleted,
    avgMood,
    avgEnergy,
    avgStress,
    text,
  };
}
